import { Banner, BlockStack, Box, Card, DescriptionList, Divider, EmptyState, InlineStack, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import { TypedOrders, useGetOrdersUnExportInvoiceToday } from 'queries/orders.query';
import __helpers from 'helpers/index';
import dayjs from 'dayjs';
import { useSettings } from 'context/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDot } from '@fortawesome/free-solid-svg-icons';
import Lottie from 'lottie-react';
import work_from_home from 'media/lottie_files/work_from_home.json';

export default function UnExportInvoiceToday() {
  const { settings, loading: loadingSetting } = useSettings();
  const { data, isLoading, isError, refetch } = useGetOrdersUnExportInvoiceToday();

  /*
   * Giả sử API trả về trực tiếp một array.
   * Nếu Axios trả về { data: [...] } thì sửa thành:
   *
   * const orders = data?.data ?? [];
   */
  const orders: TypedOrders[] = data ?? [];

  if (isLoading) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Đơn hàng chưa xuất hóa đơn
          </Text>

          <SkeletonBodyText lines={2} />
        </BlockStack>
      </Card>
    );
  }

  if (isError) {
    return (
      <Banner
        title="Không tải được danh sách đơn chưa xuất hóa đơn"
        tone="critical"
        action={{
          content: 'Thử lại',
          onAction: () => refetch(),
        }}
      >
        Vui lòng kiểm tra kết nối hoặc tải lại dữ liệu.
      </Banner>
    );
  }

  const previewOrders = orders.slice(0, 5);
  const remainingCount = orders.length - previewOrders.length;

  return (
    <Banner title={orders.length > 0 ? `${orders.length} đơn chưa xuất` : 'ĐÃ XUẤT HẾT HÓA ĐƠN'} tone={orders.length > 0 ? 'warning' : 'success'}>
      <BlockStack gap="100">
        {orders.length > 0 ? (
          <Text as="p">Kế toán cần kiểm tra và xuất hóa đơn cho các đơn hàng sau:</Text>
        ) : (
          <EmptyState image={''} footerContent={'Chưa có đơn hàng nào cần xuất, có tui báo!'}>
            <InlineStack align="center">
              <Box padding={'400'} width="200px">
                <Lottie animationData={work_from_home} loop />
              </Box>
            </InlineStack>
          </EmptyState>
        )}

        {orders.length > 0 ? (
          <DescriptionList
            gap="tight"
            items={
              previewOrders
                ? previewOrders.map((order, index) => {
                    return {
                      term: order.order_code,
                      description: (
                        <Link
                          removeUnderline
                          key={`link_homepage___A_remind_index_` + index}
                          url={`/ticket_managers?d=${dayjs(Number(order.trip_manifest?.departure_date)).tz('Asia/Ho_Chi_Minh').format('YYYY-MM-DD')}&journey_id=${order?.journey_id}&h=${order?.trip_manifest?.time_id}#${order.id}`}
                        >
                          Chuyến đi lúc {order?.trip_manifest?.departure_hour} ngày{' '}
                          {dayjs(Number(order.trip_manifest?.departure_date)).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY')} /{' '}
                          {order?.journey?.journey_name}{' '}
                        </Link>
                      ),
                    };
                  })
                : []
            }
          />
        ) : null}

        {remainingCount > 0 && <Text as="p" tone="critical">{`+ ${__helpers.formatNumberWithCommas(remainingCount)} đơn khác`}</Text>}
        <Divider />

        <BlockStack>
          {loadingSetting ? (
            <SkeletonBodyText />
          ) : settings?._auto_sign_invoice_before_midnight === '1' ? (
            <Text as="p">
              <FontAwesomeIcon icon={faCircleDot} color="green" /> Hệ thống có ký tự động trước 23h30p hàng ngày.{' '}
              {settings?._last_auto_sign_invoice_before_midnight_result ? settings?._last_auto_sign_invoice_before_midnight_result : ''}
            </Text>
          ) : (
            <Text as="p" tone="critical">
              <FontAwesomeIcon icon={faCircleDot} color="red" /> Hệ thống đang không ký hóa đơn tự động!
            </Text>
          )}
          {loadingSetting ? (
            <SkeletonBodyText />
          ) : settings?.__auto_create_draft_invoice_before_midnight === '1' ? (
            <Text as="p">
              <FontAwesomeIcon icon={faCircleDot} color="green" /> Hệ thống có xuất hóa đơn nháp trước 23h hàng ngày.{' '}
              {settings?._last_auto_create_draft_invoice_before_midnight_result
                ? settings?._last_auto_create_draft_invoice_before_midnight_result
                : ''}
            </Text>
          ) : (
            <Text as="p" tone="critical">
              <FontAwesomeIcon icon={faCircleDot} color="red" /> Hệ thống đang không xuất hóa đơn nháp tự động!
            </Text>
          )}
        </BlockStack>
      </BlockStack>
    </Banner>
  );
}

